import {
  Stack,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  Autocomplete,
  TextField,
} from "@mui/material";
import React from "react";

import { useParams } from "react-router-dom";

import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import { DataGridPremium, GridToolbar } from "@mui/x-data-grid-premium";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";

const QaQueueManage = () => {
  const { id } = useParams();
  const [queues, setQueues] = React.useState([]);
  const [allQueues, setAllQueues] = React.useState([]);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedQueue, setSelectedQueue] = React.useState("");
  const [defaultExists, setDefaultExists] = React.useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [queueToDelete, setQueueToDelete] = React.useState(null);

  // Add these new state variables
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [settingDefaultQueue, setSettingDefaultQueue] = React.useState(null);

  let getQueues4Campaign = useQuery({
    queryKey: ["getQueuess", id],
    queryFn: async () =>
      await axios.get(
        `${process.env.REACT_APP_API_URL}/QAInbound/admin/campaign/callcentercampaign/${id}`,
      ),
    onSuccess: (data) => {
      setQueues(
        data?.data?.data?.filter(
          (campaignQueue) => campaignQueue?.qa_campaign?.active === true,
        ),
      );
      setDefaultExists(
        data?.data?.data?.some(
          (campaignQueue) => campaignQueue?.default_queue === true,
        ),
      );
      // console.log("defaultExists: ", defaultExists);
      // console.log("getCampaigns data: ", data);
    },
  });

  let getAllQueues = useQuery({
    queryKey: ["getAllQueues"],
    queryFn: async () =>
      await axios.get(`${process.env.REACT_APP_API_URL}/QAInbound/admin`),
    onSuccess: (data) => {
      setAllQueues(
        data?.data?.data?.filter(
          (campaignQueue) => campaignQueue?.active === true,
        ),
      );
      // console.log("allQeues data: ", data);
    },
  });

  const handleAddQueue = async (qaQueueId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/QAInbound/admin/campaign/${qaQueueId}/callcentercampaign`,
        { campaign_id: id, default_queue: !defaultExists },
      );
      // Refresh both queries
      getQueues4Campaign.refetch();
      getAllQueues.refetch();
      setOpenDialog(false);
    } catch (error) {
      console.error("Error linking queue:", error);
    }
  };

  const handleSubmit = () => {
    if (selectedQueue) {
      handleAddQueue(selectedQueue);
    }
  };

  const handleDeleteQueue = async (qaQueueId, campaignLinkingId) => {
    try {
      setIsDeleting(true);
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/QAInbound/admin/campaign/${qaQueueId}/callcentercampaign/cc/${campaignLinkingId}`,
      );
      // Refresh the queues list
      await getQueues4Campaign.refetch();
      setDeleteDialogOpen(false);
      setQueueToDelete(null);
    } catch (error) {
      console.error("Error deleting queue link:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSetDefaultQueue = async (qaQueueId) => {
    try {
      setSettingDefaultQueue(qaQueueId);
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/QAInbound/admin/campaign/callcentercampaign/${id}`,
        { id: qaQueueId },
      );
      // Refresh the queues list
      await getQueues4Campaign.refetch();
    } catch (error) {
      console.error("Error setting default queue:", error);
    } finally {
      setSettingDefaultQueue(null);
    }
  };

  // Filter out already linked queues
  const availableQueues = allQueues.filter(
    (queue) =>
      !queues.some((linkedQueue) => linkedQueue.qa_campaign?.id === queue.id),
  );

  if (getQueues4Campaign.isLoading || getAllQueues.isLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: "400px" }}
      >
        <CircularProgress />
        <Stack sx={{ mt: 2 }}>Loading Queues...</Stack>
      </Stack>
    );
  }

  if (getQueues4Campaign.isError || getAllQueues.isError) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: "400px", color: "error.main" }}
      >
        Error loading campaigns: {getQueues4Campaign.error.message}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Button
        variant="contained"
        onClick={() => setOpenDialog(true)}
        sx={{ alignSelf: "flex-end", mb: 2 }}
      >
        Link New Queue
      </Button>

      {queues && queues.length > 0 && (
        <div style={{ height: 400, width: "100%" }}>
          <DataGridPremium
            autoHeight
            rows={queues}
            initialState={{
              sorting: {
                sortModel: [{ field: "default_queue", sort: "desc" }],
              },
            }}
            columns={[
              {
                field: "qa_campaign.name",
                headerName: "Queue Name",
                flex: 1,
                valueGetter: (params) => params.row.qa_campaign?.name || "",
              },
              {
                field: "default_queue",
                headerName: "Default Queue",
                flex: 1,
                sortable: true,
                renderCell: (params) =>
                  params.row.default_queue ? (
                    <CheckCircleIcon color="primary" />
                  ) : null,
              },
              {
                field: "actions",
                headerName: "Actions",
                width: 200,
                renderCell: (params) => (
                  <Stack direction="row" spacing={1}>
                    <Button
                      color="error"
                      disabled={isDeleting}
                      onClick={() => {
                        setQueueToDelete(params.row);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      {isDeleting && queueToDelete?.id === params.row.id ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <DeleteIcon />
                      )}
                    </Button>
                    {!params.row.default_queue && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        disabled={settingDefaultQueue !== null}
                        onClick={() => handleSetDefaultQueue(params.row.id)}
                      >
                        {settingDefaultQueue === params.row.id ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          "Set Default"
                        )}
                      </Button>
                    )}
                  </Stack>
                ),
              },
            ]}
            slots={{ toolbar: GridToolbar }}
          />
        </div>
      )}

      {/* Modified Queue Selection Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedQueue("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Queue to Link</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Autocomplete
              id="queue-select"
              options={availableQueues}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={
                availableQueues.find((queue) => queue.id === selectedQueue) ||
                null
              }
              onChange={(event, newValue) => {
                setSelectedQueue(newValue?.id || "");
              }}
              renderInput={(params) => (
                <TextField {...params} label="Queue" variant="outlined" />
              )}
              renderOption={(props, option) => (
                <MenuItem {...props} value={option.id}>
                  {option.name}
                </MenuItem>
              )}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSelectedQueue("");
              setOpenDialog(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!selectedQueue}
          >
            Link Queue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add the Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setQueueToDelete(null);
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to unlink the queue "
          {queueToDelete?.qa_campaign?.name}"?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setQueueToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={isDeleting}
            onClick={() =>
              handleDeleteQueue(
                queueToDelete?.qa_campaign_id,
                queueToDelete?.id,
              )
            }
          >
            {isDeleting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default QaQueueManage;
